import { useCallback, useEffect, useRef, useState } from 'react'
import {
  agentApi,
  type AgentAttachment,
  type AgentConfirmation,
  type AgentEvent,
  type AgentMessage,
  type AgentProposal,
  type AgentToolCall,
} from '@/lib/api/agent'
import {
  isBlankConversation,
  mergeKeptCalls,
  mergeProposals,
  samePage,
  type PageContext,
} from '@/src/lib/assistant/session'
import { deliverMessage, friendlyError, isConnectionError } from '@/src/lib/assistant/delivery'
import type { ToolCallView } from './ToolCallCard'
import { useVoiceConversation } from './useVoiceConversation'

interface Options {
  threadId: string | null
  onThreadCreated: (threadId: string) => void
  /** Beacon gave the conversation a title; the list can show it at once. */
  onThreadTitled?: (threadId: string, title: string) => void
  contextType?: string
  contextId?: string
  /** The page the person is on, told to Beacon with each message and during a call. */
  page?: PageContext | null
}

/** Persisted tool calls arrive in the API shape; the card wants a view. */
function toView(call: AgentToolCall): ToolCallView {
  return {
    id: call.id,
    runId: call.run_id,
    toolName: call.tool_name,
    status:
      call.status === 'FAILED' ? 'failed' : call.status === 'PENDING' ? 'running' : 'completed',
    result: call.result ?? undefined,
    error: call.error ?? undefined,
  }
}

/**
 * One conversation with Beacon, in text and by voice: what has been said, what
 * is streaming, what waits for approval, and the call if there is one.
 *
 * A hook rather than component state so it can live wherever the conversation
 * has to outlive the screen showing it. Helm keeps one in the session above
 * the routes, so the widget and the full page share it and moving between
 * pages neither resets it nor hangs up.
 *
 * Streamed text is held apart and only merged into the message list when the
 * run completes, so a failed run does not leave a half-written reply looking
 * like a real answer.
 */
export function useAssistant({
  threadId,
  onThreadCreated,
  onThreadTitled,
  contextType,
  contextId,
  page = null,
}: Options) {
  const [messages, setMessages] = useState<AgentMessage[]>([])
  const [confirmations, setConfirmations] = useState<AgentConfirmation[]>([])
  const [proposals, setProposals] = useState<AgentProposal[]>([])
  const [toolCalls, setToolCalls] = useState<ToolCallView[]>([])
  const [attachments, setAttachments] = useState<AgentAttachment[]>([])
  const [uploading, setUploading] = useState(false)
  const [model, setModel] = useState<string | null>(null)
  const [streamingText, setStreamingText] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Files attached since the last message: Beacon reads them with the next one. */
  const [fresh, setFresh] = useState<string[]>([])
  /** A message whose connection dropped and could not be picked up again: Retry finishes it. */
  const [lost, setLost] = useState<{ content: string; sentAt: number } | null>(null)
  /** The connection dropped mid-answer and Helm is finding out what reached Beacon. */
  const [reconnecting, setReconnecting] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  /** A thread created here before the owner has passed it back down. */
  const createdThread = useRef<string | null>(null)
  /** The conversation this assistant started (by text, a file or a call), until another opens. */
  const [startedThread, setStartedThread] = useState<string | null>(null)
  /** The run currently streaming, so its tool calls can be placed with its reply. */
  const currentRunId = useRef<string | null>(null)
  const pageRef = useRef(page)
  useEffect(() => {
    pageRef.current = page
  }, [page])

  useEffect(() => {
    // Made here a moment ago for what is being sent: everything in it is
    // already on screen, and loading it now could land after the reply has
    // started streaming in and wipe it.
    if (threadId && threadId === createdThread.current) return
    // Another conversation, or a new one: a thread made for the previous one
    // must not catch the next message.
    createdThread.current = null
    if (!threadId) {
      setMessages([])
      setConfirmations([])
      setProposals([])
      setToolCalls([])
      setAttachments([])
      setFresh([])
      setModel(null)
      setError(null)
      setLost(null)
      return
    }
    let cancelled = false
    agentApi
      .listAttachments(threadId)
      .then((list) => !cancelled && setAttachments(list))
      .catch(() => undefined)
    agentApi
      .getThread(threadId)
      .then((thread) => {
        if (cancelled) return
        setMessages(thread.messages.filter((message) => message.role !== 'SYSTEM'))
        setConfirmations(thread.confirmations)
        setProposals(thread.proposals ?? [])
        setToolCalls(thread.tool_calls.map(toView))
        setModel(thread.model)
      })
      .catch((cause) => !cancelled && setError(friendlyError(cause)))
    return () => {
      cancelled = true
    }
  }, [threadId])

  const titled = useRef(onThreadTitled)
  useEffect(() => {
    titled.current = onThreadTitled
  }, [onThreadTitled])

  const handleEvent = useCallback((event: AgentEvent) => {
    if (event.type === 'thread.titled') {
      titled.current?.(event.thread_id, event.title)
    } else if (event.type === 'run.started') {
      currentRunId.current = event.run_id
    } else if (event.type === 'text.delta') {
      setStreamingText((text) => text + event.delta)
    } else if (event.type === 'tool.started') {
      setToolCalls((calls) => [
        ...calls,
        {
          id: event.tool_call_id,
          runId: currentRunId.current,
          toolName: event.tool_name,
          status: 'running',
        },
      ])
    } else if (event.type === 'tool.completed') {
      // Proposed actions show as their card at once, before the reply is done.
      const proposed = (event.result as { proposals?: AgentProposal[] } | null)?.proposals
      if (event.tool_name === 'propose_actions' && Array.isArray(proposed)) {
        setProposals((existing) => mergeProposals(existing, proposed))
      }
      setToolCalls((calls) =>
        calls.map((call) =>
          call.id === event.tool_call_id
            ? { ...call, status: 'completed', result: event.result }
            : call,
        ),
      )
    } else if (event.type === 'tool.failed') {
      setToolCalls((calls) =>
        calls.map((call) =>
          call.id === event.tool_call_id ? { ...call, status: 'failed', error: event.error } : call,
        ),
      )
    } else if (event.type === 'confirmation.required') {
      setConfirmations((existing) => [...existing, event.confirmation])
    } else if (event.type === 'run.failed') {
      setError(event.error)
    }
  }, [])

  /** The conversation to work in, started on first need: a pasted screenshot can open one. */
  const ensureThread = useCallback(
    async (title: string) => {
      const existing = threadId ?? createdThread.current
      if (existing) return existing
      const thread = await agentApi.createThread({
        title: title.slice(0, 60),
        context_type: contextType,
        context_id: contextId,
      })
      createdThread.current = thread.id
      setStartedThread(thread.id)
      onThreadCreated(thread.id)
      return thread.id
    },
    [threadId, contextType, contextId, onThreadCreated],
  )

  /**
   * Sends a message and shows the answer. On a phone the connection drops: a
   * dropped stream is followed by reading the conversation until the answer
   * is kept there, and a message that never arrived is sent again. If neither
   * works the message stays on screen, the files stay attached, and Retry
   * picks it up where it got to.
   */
  const deliver = useCallback(
    async (content: string, sentAt: number, resume: boolean) => {
      setError(null)
      setLost(null)
      setStreaming(true)
      setStreamingText('')

      const controller = new AbortController()
      abortRef.current = controller

      try {
        const activeThreadId = await ensureThread(content)
        const read = () => agentApi.getThread(activeThreadId)
        const outcome = await deliverMessage(
          content,
          sentAt,
          {
            stream: () =>
              agentApi.streamMessage(
                activeThreadId,
                content,
                handleEvent,
                controller.signal,
                pageRef.current,
              ),
            read,
            // Stop ends the wait for an answer too, not only the stream.
            wait: (ms) =>
              new Promise<void>((resolve, reject) => {
                if (controller.signal.aborted) {
                  reject(new DOMException('Stopped', 'AbortError'))
                  return
                }
                const timer = setTimeout(resolve, ms)
                controller.signal.addEventListener(
                  'abort',
                  () => {
                    clearTimeout(timer)
                    reject(new DOMException('Stopped', 'AbortError'))
                  },
                  { once: true },
                )
              }),
            onReconnecting: () => {
              setReconnecting(true)
              // Half an answer from the dropped stream; the whole one is coming.
              setStreamingText('')
            },
          },
          { resume },
        )
        // Refetch rather than trusting local state: the server is the record,
        // and it holds the persisted message ids and any new confirmations.
        const thread = await read()
        setMessages(thread.messages.filter((message) => message.role !== 'SYSTEM'))
        setConfirmations(thread.confirmations)
        setProposals((existing) => mergeProposals(existing, thread.proposals ?? []))
        // Calls seen in a stream that dropped never finished on screen; the kept ones did.
        if (outcome === 'recovered') setToolCalls(thread.tool_calls.map(toView))
        setFresh([])
      } catch (cause) {
        if ((cause as Error).name === 'AbortError') return
        if (isConnectionError(cause)) setLost({ content, sentAt })
        else setError(friendlyError(cause))
      } finally {
        setStreaming(false)
        setReconnecting(false)
        setStreamingText('')
        abortRef.current = null
      }
    },
    [ensureThread, handleEvent],
  )

  const send = useCallback(
    async (content: string) => {
      // Shown at once, before a new conversation even exists, so the first
      // message moves straight into the thread rather than waiting on it.
      const optimistic: AgentMessage = {
        id: `local-${Date.now()}`,
        thread_id: threadId ?? createdThread.current ?? '',
        role: 'USER',
        content,
        references: [],
        run_id: null,
        created_at: new Date().toISOString(),
      }
      setMessages((existing) => [...existing, optimistic])
      await deliver(content, Date.now(), false)
    },
    [threadId, deliver],
  )

  /** After a lost connection: finds where the message got to, and finishes it. */
  const retry = useCallback(async () => {
    if (!lost) return
    await deliver(lost.content, lost.sentAt, true)
  }, [lost, deliver])

  const stop = useCallback(() => abortRef.current?.abort(), [])

  const refresh = useCallback(async () => {
    if (!threadId) return
    const thread = await agentApi.getThread(threadId)
    setConfirmations(thread.confirmations)
    setMessages(thread.messages.filter((message) => message.role !== 'SYSTEM'))
    // What was proposed, or a reply drafted, during a call reaches the screen this way.
    setProposals((existing) => mergeProposals(existing, thread.proposals ?? []))
    setToolCalls((calls) => mergeKeptCalls(calls, thread.tool_calls.map(toView)))
  }, [threadId])

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return
      setUploading(true)
      setError(null)
      try {
        const first = files[0]!
        const id = await ensureThread(first.type.startsWith('image/') ? 'Screenshot' : first.name)
        for (const file of files) {
          const created = await agentApi.uploadAttachment(id, file)
          setAttachments((existing) => [created, ...existing])
          setFresh((ids) => [...ids, created.id])
        }
      } catch (cause) {
        // A rejected type or an oversized file is worth saying out loud rather
        // than leaving the operator to wonder whether it arrived. Files already
        // attached stay attached.
        setError(
          isConnectionError(cause)
            ? 'Lost connection to Beacon while attaching. The files shown are attached; add the rest again.'
            : friendlyError(cause, 'The file was not attached.'),
        )
      } finally {
        setUploading(false)
      }
    },
    [ensureThread],
  )

  const voice = useVoiceConversation({
    onThreadCreated: (id) => {
      setStartedThread(id)
      onThreadCreated(id)
    },
    onThreadChanged: () => void refresh().catch(() => undefined),
  })
  // A call belongs to its thread: moving to another one hangs up.
  const {
    active: voiceActive,
    threadId: voiceThreadId,
    stop: stopVoice,
    start: startCall,
    notePage,
  } = voice
  useEffect(() => {
    if (voiceActive && voiceThreadId && threadId && threadId !== voiceThreadId) stopVoice()
  }, [threadId, voiceActive, voiceThreadId, stopVoice])

  const startVoice = useCallback(
    () => void startCall(threadId, pageRef.current),
    [startCall, threadId],
  )

  // In a call, moving to another record is worth a quiet word, so "this one"
  // means the record now on screen. Said once per page, and never out loud.
  const toldPage = useRef<PageContext | null>(null)
  useEffect(() => {
    if (!voiceActive) {
      toldPage.current = null
      return
    }
    if (!page) return
    if (toldPage.current === null) {
      // The call started on this page and was told so when it opened.
      toldPage.current = page
      return
    }
    if (samePage(toldPage.current, page)) return
    toldPage.current = page
    notePage(page)
  }, [voiceActive, page, notePage])

  const summaryOf = useCallback(
    (toolCallId: string) =>
      confirmations.find((item) => item.tool_call_id === toolCallId)?.summary ?? 'the change',
    [confirmations],
  )

  const confirm = useCallback(
    async (toolCallId: string, second: boolean) => {
      try {
        await agentApi.confirmToolCall(toolCallId, second)
        // In a call, the assistant hears what was decided and can say so.
        voice.reportDecision(summaryOf(toolCallId), true)
      } catch (cause) {
        voice.reportDecision(
          summaryOf(toolCallId),
          true,
          cause instanceof Error ? cause.message : null,
        )
        throw cause
      } finally {
        await refresh()
      }
    },
    [voice, summaryOf, refresh],
  )

  /**
   * Approves proposals, one at a time and in order, each possibly edited. In a
   * call, Beacon hears what was done, and what was not and why.
   */
  const approveProposals = useCallback(
    async (ids: string[], edits: Record<string, Record<string, unknown>> = {}) => {
      for (const id of ids) {
        setProposals((existing) =>
          existing.map((proposal) =>
            proposal.id === id ? { ...proposal, status: 'EXECUTING' } : proposal,
          ),
        )
        try {
          const decided = await agentApi.approveProposal(id, edits[id] ?? null)
          setProposals((existing) => mergeProposals(existing, [decided]))
          voice.reportDecision(
            decided.summary,
            true,
            decided.status === 'EXECUTED' ? null : (decided.error ?? 'It was not done'),
          )
        } catch (cause) {
          // Not carried out: it stays waiting, with the reason beside it.
          const message = isConnectionError(cause)
            ? 'Lost connection to Beacon. Nothing was done; approve it again.'
            : friendlyError(cause, 'It could not be approved')
          setProposals((existing) =>
            existing.map((proposal) =>
              proposal.id === id ? { ...proposal, status: 'PENDING', error: message } : proposal,
            ),
          )
        }
      }
    },
    [voice],
  )

  const dismissProposals = useCallback(
    async (ids: string[]) => {
      for (const id of ids) {
        try {
          const decided = await agentApi.dismissProposal(id)
          setProposals((existing) => mergeProposals(existing, [decided]))
          voice.reportDecision(decided.summary, false)
        } catch {
          // Left as it was; the card still offers it.
        }
      }
    },
    [voice],
  )

  const reject = useCallback(
    async (toolCallId: string) => {
      await agentApi.rejectToolCall(toolCallId)
      voice.reportDecision(summaryOf(toolCallId), false)
      await refresh()
    },
    [voice, summaryOf, refresh],
  )

  // Nothing in it yet: no conversation, or one just started here that has
  // nothing to show (a file attached, a call under way). The full page shows
  // its home for this; anything said or proposed moves it to the thread.
  const blank = isBlankConversation({
    threadId,
    startedThread,
    messages: messages.length,
    streaming,
    confirmations: confirmations.length,
    toolCalls: toolCalls.length,
    proposals: proposals.length,
  })

  return {
    threadId,
    blank,
    lost: lost !== null,
    reconnecting,
    retry,
    messages,
    confirmations,
    proposals,
    toolCalls,
    attachments,
    uploading,
    model,
    streamingText,
    streaming,
    error,
    fresh,
    send,
    stop,
    refresh,
    uploadFiles,
    confirm,
    reject,
    approveProposals,
    dismissProposals,
    voice,
    startVoice,
  }
}

export type AssistantController = ReturnType<typeof useAssistant>
